import { Attachment } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { FileView } from 'chat/messages/file/view'
import { showShareHeaderButton } from 'chat/shared/header_share_button'
import { TaskDetailsPhotoViewerScreenProps } from 'home/stack/types'
import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderTitle } from 'shared/header/header_title'
import { shareAttachment } from 'shared/utils'
import { useTask } from 'tasks/queries/detail'

export function TaskDetailsPhotoViewerScreen({
  navigation,
  route,
}: TaskDetailsPhotoViewerScreenProps) {
  const { taskId, fromDate, toDate, uri } = route.params
  const { theme } = useTheme()
  const [isShareLoading, setShareLoading] = useState(false)

  const [attachmentIndex, setAttachmentIndex] = useState<number | undefined>()
  const [url, setUrl] = useState<string | undefined>(uri)

  const taskQuery = useTask(taskId, { fromDate, toDate })

  const taskPhotoAttachments: Attachment[] = useMemo(() => {
    if (taskQuery.data?.occurrences?.[0]?.urls) {
      return taskQuery.data.occurrences[0].urls.map(sourceUrl => ({
        uri: sourceUrl,
        mimeType: 'image/*', //Using generic image MIME type
        type: 'media',
      }))
    }
    return []
  }, [taskQuery.data?.occurrences])

  const onShareAttachment = useCallback(async () => {
    setShareLoading(true)
    if (taskPhotoAttachments && attachmentIndex !== undefined) {
      const currentAttachment = taskPhotoAttachments[attachmentIndex]
      if (currentAttachment) {
        await shareAttachment(currentAttachment)
      }
    }
    setShareLoading(false)
  }, [attachmentIndex, taskPhotoAttachments])

  useLayoutEffect(() => {
    if (attachmentIndex === undefined && url) {
      const currentAttachmentIndex = taskPhotoAttachments?.findIndex(
        attachment => attachment?.uri === url
      )
      setAttachmentIndex(
        currentAttachmentIndex >= 0 ? currentAttachmentIndex : undefined
      )
    }
  }, [attachmentIndex, taskPhotoAttachments, url])

  useLayoutEffect(() => {
    if (taskPhotoAttachments && !isNaN(Number(attachmentIndex))) {
      const attachmentUrl = taskPhotoAttachments[attachmentIndex as number]?.uri
      if (attachmentUrl && url !== attachmentUrl) {
        setUrl(attachmentUrl)
      }
    }
  }, [attachmentIndex, taskPhotoAttachments, url])

  useLayoutEffect(() => {
    const title = t('taskPhotos')
    showHeaderTitle(navigation, title)
  }, [navigation])

  useLayoutEffect(() => {
    if (taskPhotoAttachments && attachmentIndex !== undefined) {
      const currentAttachment = taskPhotoAttachments[attachmentIndex]
      if (currentAttachment) {
        showShareHeaderButton({
          navigation,
          theme,
          onPress: onShareAttachment,
          loading: isShareLoading,
        })
      }
    }
  }, [
    attachmentIndex,
    isShareLoading,
    navigation,
    onShareAttachment,
    taskPhotoAttachments,
    theme,
  ])

  return (
    <StatusBoundary
      isLoading={taskQuery.isLoading}
      error={taskQuery.error}
    >
      <FileView
        attachments={taskPhotoAttachments}
        setCurrentIndex={setAttachmentIndex}
        currentIndex={attachmentIndex}
      />
    </StatusBoundary>
  )
}
